/**
 * AdminUserDrawer — ADR-0022.
 *
 * Drawer szczegolow administratora platformy. Otwierany z `AdminUsers` po kliknieciu
 * "Szczegoly" w wierszu tabeli. Tylko super-admin (lista jest gated 403, drawer
 * korzysta z tych samych endpointow).
 *
 * Sekcje:
 *  1. Read-only header — id, totp status, last login, created.
 *  2. Edit form — name, role, isActive (PATCH /admin/admins/:id).
 *  3. Login data — email + username (POST /admin/admins/:id/change-identifier).
 *  4. Reset password — przycisk wysylajacy link do targeta (POST /admin/admins/:id/reset-password).
 *
 * Self-edit: gdy currentAdminId === target.id, blokujemy zmiane roli i isActive
 * (zeby nie zdegradowac/zdezaktywowac samego siebie). Na zmiany identyfikatora
 * jest osobna strona /profile (krok 14, password challenge required).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidUsername } from '@saas/shared';
import type { AxiosError } from 'axios';

import { AdminUserRolesSection } from '@/components/AdminUserRolesSection';
import { Button } from '@/components/Button';
import { Drawer } from '@/components/Drawer';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import type { AdminRole } from './AdminUserInviteModal';

export interface AdminUserDetail {
  id: string;
  email: string;
  username: string;
  name: string | null;
  role: AdminRole;
  isActive: boolean;
  totpEnabled: boolean;
  require2faSetup: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  invitedAt: string | null;
  invitationExpiresAt: string | null;
}

export interface AdminUserDrawerProps {
  open: boolean;
  onClose: () => void;
  /** ID admina ktorego otwieramy (null gdy drawer zamkniety). */
  adminId: string | null;
  /** ID aktualnie zalogowanego admina — do self-edit guard. */
  currentAdminId: string | null;
  /** Wywolywane gdy zaszly zmiany — caller refetch'uje liste. */
  onChanged: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDateTime(iso: string | null, fallback: string): string {
  if (!iso) return fallback;
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminUserDrawer({
  open,
  onClose,
  adminId,
  currentAdminId,
  onChanged,
}: AdminUserDrawerProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Edit form state.
  const [name, setName] = useState('');
  const [role, setRole] = useState<AdminRole>('readonly');
  const [isActive, setIsActive] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileApiError, setProfileApiError] = useState<string | null>(null);

  // Identifier change state.
  const [newEmail, setNewEmail] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [savingIdentifier, setSavingIdentifier] = useState(false);
  const [identifierFieldErrors, setIdentifierFieldErrors] = useState<Record<string, string>>({});
  const [identifierApiError, setIdentifierApiError] = useState<string | null>(null);

  // Reset password state.
  const [resetingPassword, setResetingPassword] = useState(false);

  const isSelf = currentAdminId !== null && detail !== null && detail.id === currentAdminId;

  // Load detail when drawer opens.
  useEffect(() => {
    if (!open || !adminId) {
      setDetail(null);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setProfileApiError(null);
    setIdentifierApiError(null);
    setIdentifierFieldErrors({});
    api
      .get<AdminUserDetail>(`/admin/admins/${adminId}`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        setDetail(d);
        setName(d.name ?? '');
        setRole(d.role);
        setIsActive(d.isActive);
        setNewEmail(d.email);
        setNewUsername(d.username);
      })
      .catch((err: AxiosError<{ error: string }>) => {
        if (cancelled) return;
        setLoadError(
          err.response?.status === 404
            ? t('admin.adminUsers.drawer.errors.notFound')
            : t('admin.adminUsers.drawer.errors.loadFailed'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, adminId, t]);

  async function saveProfile() {
    if (!detail) return;
    setSavingProfile(true);
    setProfileApiError(null);

    const payload: Record<string, unknown> = {};
    const trimmedName = name.trim();
    const newName = trimmedName === '' ? null : trimmedName;
    if (newName !== detail.name) payload.name = newName;
    if (!isSelf && role !== detail.role) payload.role = role;
    if (!isSelf && isActive !== detail.isActive) payload.isActive = isActive;

    if (Object.keys(payload).length === 0) {
      setSavingProfile(false);
      onClose();
      return;
    }

    try {
      await api.patch(`/admin/admins/${detail.id}`, payload);
      toast.show({
        variant: 'success',
        title: t('admin.adminUsers.drawer.profile.savedTitle'),
        description: t('admin.adminUsers.drawer.profile.savedDescription'),
      });
      onChanged();
      onClose();
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      if (code === 'LAST_SUPER_ADMIN') {
        setProfileApiError(t('admin.adminUsers.drawer.profile.errors.lastSuperAdmin'));
      } else if (axErr.response?.status === 403) {
        setProfileApiError(t('admin.adminUsers.drawer.profile.errors.forbidden'));
      } else {
        setProfileApiError(t('admin.adminUsers.drawer.profile.errors.unknown'));
      }
      setSavingProfile(false);
      return;
    }
    setSavingProfile(false);
  }

  function validateIdentifier(): boolean {
    if (!detail) return false;
    const errors: Record<string, string> = {};
    const e = newEmail.trim().toLowerCase();
    const u = newUsername.trim();

    if (!e) {
      errors.email = t('admin.adminUsers.drawer.identifier.errors.emailRequired');
    } else if (!EMAIL_REGEX.test(e)) {
      errors.email = t('admin.adminUsers.drawer.identifier.errors.emailFormat');
    }
    if (!u) {
      errors.username = t('admin.adminUsers.drawer.identifier.errors.usernameRequired');
    } else if (!isValidUsername(u)) {
      errors.username = t('admin.adminUsers.drawer.identifier.errors.usernameFormat');
    }

    if (e === detail.email && u === detail.username) {
      errors.username = t('admin.adminUsers.drawer.identifier.errors.noChange');
    }

    setIdentifierFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function changeIdentifier() {
    if (!detail) return;
    if (!validateIdentifier()) return;

    setSavingIdentifier(true);
    setIdentifierApiError(null);

    const payload: Record<string, string> = {};
    const e = newEmail.trim().toLowerCase();
    const u = newUsername.trim();
    if (e !== detail.email) payload.email = e;
    if (u !== detail.username) payload.username = u;

    try {
      await api.post(`/admin/admins/${detail.id}/change-identifier`, payload);
      toast.show({
        variant: 'success',
        title: t('admin.adminUsers.drawer.identifier.savedTitle'),
        description: t('admin.adminUsers.drawer.identifier.savedDescription'),
      });
      onChanged();
      onClose();
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      if (code === 'EMAIL_ALREADY_EXISTS') {
        setIdentifierFieldErrors((prev) => ({
          ...prev,
          email: t('admin.adminUsers.drawer.identifier.errors.emailTaken'),
        }));
      } else if (code === 'USERNAME_ALREADY_EXISTS') {
        setIdentifierFieldErrors((prev) => ({
          ...prev,
          username: t('admin.adminUsers.drawer.identifier.errors.usernameTaken'),
        }));
      } else {
        setIdentifierApiError(t('admin.adminUsers.drawer.identifier.errors.unknown'));
      }
      setSavingIdentifier(false);
      return;
    }
    setSavingIdentifier(false);
  }

  async function triggerResetPassword() {
    if (!detail) return;
    setResetingPassword(true);
    try {
      await api.post(`/admin/admins/${detail.id}/reset-password`);
      toast.show({
        variant: 'success',
        title: t('admin.adminUsers.drawer.reset.sentTitle'),
        description: t('admin.adminUsers.drawer.reset.sentDescription', { email: detail.email }),
      });
    } catch {
      toast.show({
        variant: 'error',
        title: t('admin.adminUsers.drawer.reset.errors.unknownTitle'),
        description: t('admin.adminUsers.drawer.reset.errors.unknownDescription'),
      });
    }
    setResetingPassword(false);
  }

  const roleOptions: { value: AdminRole; label: string }[] = [
    { value: 'super_admin', label: t('admin.adminUsers.role.super_admin') },
    { value: 'support', label: t('admin.adminUsers.role.support') },
    { value: 'finance', label: t('admin.adminUsers.role.finance') },
    { value: 'readonly', label: t('admin.adminUsers.role.readonly') },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={detail ? detail.email : t('admin.adminUsers.drawer.title')}
      description={
        detail ? `${t('admin.adminUsers.drawer.descriptionPrefix')} ${detail.username}` : undefined
      }
    >
      {loading && (
        <div className="text-sm text-text-muted" data-testid="admin-drawer-loading">
          {t('admin.adminUsers.drawer.loading')}
        </div>
      )}

      {loadError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          data-testid="admin-drawer-load-error"
        >
          {loadError}
        </div>
      )}

      {detail && !loading && !loadError && (
        <div className="flex flex-col gap-6">
          {/* Read-only meta */}
          <section
            className="space-y-1 rounded-lg border border-border-secondary bg-bg-secondary p-3 text-xs text-text-secondary"
            data-testid="admin-drawer-meta"
          >
            <div>
              <span className="font-semibold">{t('admin.adminUsers.drawer.meta.id')}:</span>{' '}
              <span className="font-mono">{detail.id}</span>
            </div>
            <div>
              <span className="font-semibold">{t('admin.adminUsers.drawer.meta.twoFactor')}:</span>{' '}
              {detail.totpEnabled
                ? t('admin.adminUsers.twoFactor.enabled')
                : detail.require2faSetup
                  ? t('admin.adminUsers.twoFactor.setupRequired')
                  : t('admin.adminUsers.twoFactor.disabled')}
            </div>
            <div>
              <span className="font-semibold">{t('admin.adminUsers.drawer.meta.lastLogin')}:</span>{' '}
              {formatDateTime(detail.lastLoginAt, t('admin.adminUsers.noLastLogin'))}
            </div>
            <div>
              <span className="font-semibold">{t('admin.adminUsers.drawer.meta.created')}:</span>{' '}
              {formatDateTime(detail.createdAt, '—')}
            </div>
          </section>

          {/* Profile edit */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-text-primary">
              {t('admin.adminUsers.drawer.profile.title')}
            </h3>
            <Input
              label={t('admin.adminUsers.drawer.profile.nameLabel')}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={savingProfile}
              data-testid="admin-drawer-name"
            />
            <FormField
              label={t('admin.adminUsers.drawer.profile.roleLabel')}
              hint={isSelf ? t('admin.adminUsers.drawer.profile.selfRoleHint') : undefined}
            >
              <Dropdown
                value={role}
                onChange={(v) => setRole(v as AdminRole)}
                options={roleOptions}
                disabled={savingProfile || isSelf}
                data-testid="admin-drawer-role"
              />
            </FormField>
            <label className="flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={savingProfile || isSelf}
                data-testid="admin-drawer-isactive"
              />
              {t('admin.adminUsers.drawer.profile.isActiveLabel')}
              {isSelf && (
                <span className="text-xs text-text-muted">
                  ({t('admin.adminUsers.drawer.profile.selfActiveHint')})
                </span>
              )}
            </label>

            {profileApiError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                data-testid="admin-drawer-profile-error"
              >
                {profileApiError}
              </div>
            )}

            <Button
              onClick={() => void saveProfile()}
              loading={savingProfile}
              disabled={savingProfile}
              data-testid="admin-drawer-profile-save"
            >
              {savingProfile
                ? t('admin.adminUsers.drawer.profile.saving')
                : t('admin.adminUsers.drawer.profile.save')}
            </Button>
          </section>

          {/* Identifier change */}
          {!isSelf && (
            <section className="space-y-3 border-t border-border-secondary pt-6">
              <h3 className="text-sm font-semibold text-text-primary">
                {t('admin.adminUsers.drawer.identifier.title')}
              </h3>
              <p className="text-xs text-text-tertiary">
                {t('admin.adminUsers.drawer.identifier.description')}
              </p>
              <Input
                label={t('admin.adminUsers.drawer.identifier.emailLabel')}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                error={identifierFieldErrors.email}
                disabled={savingIdentifier}
                data-testid="admin-drawer-identifier-email"
              />
              <Input
                label={t('admin.adminUsers.drawer.identifier.usernameLabel')}
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                error={identifierFieldErrors.username}
                disabled={savingIdentifier}
                data-testid="admin-drawer-identifier-username"
              />

              {identifierApiError && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                  data-testid="admin-drawer-identifier-error"
                >
                  {identifierApiError}
                </div>
              )}

              <Button
                variant="secondary"
                onClick={() => void changeIdentifier()}
                loading={savingIdentifier}
                disabled={savingIdentifier}
                data-testid="admin-drawer-identifier-save"
              >
                {savingIdentifier
                  ? t('admin.adminUsers.drawer.identifier.saving')
                  : t('admin.adminUsers.drawer.identifier.save')}
              </Button>
            </section>
          )}

          {/* Role ITE (RBAC multi-role, Krok 1a.4) */}
          <AdminUserRolesSection userId={detail.id} />

          {/* Reset password trigger */}
          {!isSelf && (
            <section className="space-y-3 border-t border-border-secondary pt-6">
              <h3 className="text-sm font-semibold text-text-primary">
                {t('admin.adminUsers.drawer.reset.title')}
              </h3>
              <p className="text-xs text-text-tertiary">
                {t('admin.adminUsers.drawer.reset.description', { email: detail.email })}
              </p>
              <Button
                variant="secondary"
                onClick={() => void triggerResetPassword()}
                loading={resetingPassword}
                disabled={resetingPassword}
                data-testid="admin-drawer-reset-trigger"
              >
                {resetingPassword
                  ? t('admin.adminUsers.drawer.reset.sending')
                  : t('admin.adminUsers.drawer.reset.send')}
              </Button>
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}
